import { GroupService } from './../../../../core/services/group.service';
import { DatePipe } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ShareableSourceType, entryShareKey } from 'src/app/core/models/referral.model';
import { ReferralService, referralsEnabledFor } from 'src/app/core/services/referral.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';
import { PatientAppShareDialogComponent, PatientAppShareDialogData, PatientAppShareDialogResult } from '../patient-app-share-dialog/patient-app-share-dialog.component';
import { PatientAppService, PatientAppSourceType } from '../../../../core/services/patient-app.service';
import { ShareEntryDialogComponent, ShareEntryDialogData, ShareEntryDialogResult } from '../share-entry-dialog/share-entry-dialog.component';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, interval, map, takeUntil, catchError, of, Subject, firstValueFrom } from 'rxjs';
import { ChatService } from '../../../../chat/chat.service';
import { SocketService } from '../../../../core/services/socket.service';
import { PatientChatActivity } from '../../../../chat/core/hub-types';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import type { FuseResult } from 'fuse.js';
import { ClinicalTimelineSearchIndexRecord } from './clinical-timeline-search-core';
import { applyHighlightMarks, pruneTrivialHighlightSpans } from './clinical-timeline-search-highlight';
import { ClinicalTimelineSearchService, TimelineSearchHitPayload } from './clinical-timeline-search.service';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientAdmissionHistoryService } from './../../../../core/services/patient-admission-history.service';
import { PatientCommunicationService, SendAIContentEmailRequest } from './../../../../core/services/patient-communication.service';
import { PatientLabService } from './../../../../core/services/patient-lab.service';
import { PatientNoteService } from './../../../../core/services/patient-note.service';
import { PatientService } from './../../../../core/services/patient.service';
import { PdfDownloadService } from './../../../../core/services/pdf-download.service';
import { DateDifferencePipe } from './../../../../shared/pipes/date-difference.pipe';
import { UserService } from 'src/app/core/services/user.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { checkIfGroupIsSurgical } from 'src/app/core/data/app.data';
import { WhatsAppChatDialogComponent } from '../../../../shared/components/whatsapp-chat-dialog/whatsapp-chat-dialog.component';
import { WhatsAppService } from './../../../../core/services/whatsapp.service';
import { RestoreTimelineDialogComponent } from '../../../../shared/components/restore-timeline-dialog/restore-timeline-dialog.component';
import { ClipboardService } from './../../../../core/services/clipboard.service';
import { FileDownloadService } from './../../../../core/services/file-download.service';
// tasks/practice-documents.md §6 item 3 — the fourth source. The mapping is a PURE module beside
// this one (`clinical-timeline-documents.ts`) so its one load-bearing claim — a NULL score does not
// render as 0 — can be tested without instantiating this component.
import { PatientDocumentRequestService } from './../../../../core/services/patient-document-request.service';
import {
  TIMELINE_DOCUMENT_TYPE,
  TimelineDocumentGallery,
  compileDocumentReturn,
  documentGallery,
  documentGalleryIsStale,
  documentSearchText,
  toTimelineDocuments
} from './clinical-timeline-documents';

/*
 * A returned page's SAS link lives 15 minutes (`DEFAULT_SAS_MINUTES`). These two numbers are how a
 * gallery nobody has to click open survives that.
 *
 * The sweep is deliberately far more frequent than it needs to be relative to the grace window:
 * the check is an integer comparison over the rows currently on screen, and the cost of being late
 * is a clinician looking at a blank page on a clinical record.
 */
const TIMELINE_DOCUMENT_GALLERY_SWEEP_MS = 60 * 1000;
const TIMELINE_DOCUMENT_GALLERY_GRACE_MS = 3 * 60 * 1000;

const TIMELINE_SEARCH_MIN_QUERY = 2;
const TIMELINE_SEARCH_DEBOUNCE_MS = 320;
const TIMELINE_RAW_BUILD_SLICE = 100;

@Component({
  selector: 'app-patient-clinical-timeline',
  templateUrl: './patient-clinical-timeline.component.html',
  styleUrls: ['./patient-clinical-timeline.component.scss']
})
export class PatientClinicalTimelineComponent extends BaseComponent implements OnInit, OnDestroy {

  @ViewChildren(MatMenuTrigger) menuTriggers!: QueryList<MatMenuTrigger>;

  @Input() initialExpanded?: boolean;
  expanded?: boolean;
  @Output() expandedChange = new EventEmitter<boolean>();


  timelineData!: any[] | null;
  filteredTimelineData: any[] | null = null;
  /** Groups after content + date filters only (before text search). */
  contentFilteredTimelineData: any[] = [];
  displayedTimelineData: any[] = [];
  patient: any;
  wardChanges?: any[];

  timelineSearchQuery = '';
  debouncedSearchQuery = '';
  timelineSearchIndexing = false;
  timelineSearchInFlight = false;
  private timelineSearchQuery$ = new Subject<string>();
  private currentCorpusId = 0;
  private rawCorpusBuildToken = 0;
  /** When non-null, timeline list is narrowed to these ids (empty set = no matches). */
  private searchNarrowingIds: Set<string> | null = null;
  searchHitsById = new Map<string, TimelineSearchHitPayload>();

  // Batch rendering properties
  private batchSize: number = 10; // Number of groups to render per batch
  private currentBatchIndex: number = 0; // Track current position in filtered data
  hasMoreItems: boolean = false; // Public for template access - indicates if more items can be loaded

  // Filter properties
  filters = {
    clinicalNotes: false,
    procedureNotes: false,
    billingNotes: false,
    morbidity: false,
    mortality: false,
    admissionRecords: false,
    withMedia: false
  };

  // Date range filter properties
  dateRange = {
    startDate: null as Date | null,
    endDate: null as Date | null,
    enabled: false
  };

  userProfile: any;
  canSign: boolean = false;
  isPhysician: boolean = false;
  selectedDoctor: any = {};
  groupUsers: any[] = [];
  includeSignatureOnPdf: boolean = false;
  includeSignatureOnEmail: boolean = false;
  /** When true, timeline PDFs embed non-PDF media in a 2×2 grid (up to four per page). */
  embedMediaAsGrid = false;

  /**
   * RETURNED DOCUMENTS ONLY - whether the export carries the practice's covering page.
   *
   * On by default, because the cover is what makes the export a clinical record: the letterhead,
   * the patient's demographics, the validating clinician's observations and their attachments.
   *
   * Off means the patient's returned pages and NOTHING else, which is what a clinician forwarding
   * a completed questionnaire to a third party actually wants to send - the practice's internal
   * reading of a document is not the third party's business. See `CoverPageRequest.omitCover`.
   *
   * The other record types have no cover to drop, so nothing offers this on them.
   */
  includeCoverPage = true;
  hasSignaturePermission: boolean = false;
  sendingDetails = { name: '', email: '' };

  isSurgicalSpeciality: boolean = false;

  /** When false, hide the encounter audio download action; undefined from API is treated as allowed (`!== false`). */
  canDownloadRecordings = true;
  /** Id of the timeline item whose audio is currently downloading, or null when idle. */
  downloadingAudioId: string | null = null;

  constructor(
    private patientAppService: PatientAppService,
    private router: Router,
    private groupService: GroupService,
    private patientService: PatientService,
    private patientNoteService: PatientNoteService,
    private patientLabService: PatientLabService,
    private userService: UserService,
    private patientAdmissionHistoryService: PatientAdmissionHistoryService,
    private patientCommunicationService: PatientCommunicationService,
    private pdfDownloadService: PdfDownloadService,
    private datePipe: DatePipe,
    private dateDiffPipe: DateDifferencePipe,
    private whatsappService: WhatsAppService,
    private patientDocumentRequestService: PatientDocumentRequestService,
    private cdr: ChangeDetectorRef,
    private timelineSearchService: ClinicalTimelineSearchService,
    private sanitizer: DomSanitizer,
    private clipboardService: ClipboardService,
    private http: HttpClient,
    private fileDownloadService: FileDownloadService,
    private referralService: ReferralService,
    private chatService: ChatService,
    private socketService: SocketService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    this.timelineSearchQuery$
      .pipe(debounceTime(TIMELINE_SEARCH_DEBOUNCE_MS), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((q) => {
        this.debouncedSearchQuery = q;
        this.onDebouncedTimelineSearchChange();
      });

    this.timelineSearchService.corpusReady$.pipe(takeUntil(this.destroy$)).subscribe((corpusId) => {
      if (corpusId !== this.currentCorpusId) {
        return;
      }
      this.timelineSearchIndexing = false;
      const trimmed = this.debouncedSearchQuery.trim();
      if (trimmed.length >= TIMELINE_SEARCH_MIN_QUERY) {
        this.beginTimelineSearchRequest(trimmed);
      } else {
        this.refreshTimelineViewForSearch();
      }
      this.cdr.markForCheck();
    });

    this.timelineSearchService.searchHits$.pipe(takeUntil(this.destroy$)).subscribe((payload) => {
      if (payload.corpusId !== this.currentCorpusId) {
        return;
      }
      this.timelineSearchInFlight = false;
      const map = new Map<string, TimelineSearchHitPayload>();
      for (const h of payload.hits) {
        map.set(h.id, h);
      }
      this.searchHitsById = map;
      this.searchNarrowingIds = new Set(payload.hits.map((h) => h.id));
      this.refreshTimelineViewForSearch();
      this.cdr.markForCheck();
    });

    this.patientService.currentPatient$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedPatient => {
        // Clear timeline data immediately when patient changes
        if (selectedPatient) {
          const previousPatientId = this.patient?.id;
          this.patient = selectedPatient;

          // If patient ID changed, clear timeline data
          if (previousPatientId !== selectedPatient.id) {
            this.timelineData = null;
            this.filteredTimelineData = null;
            this.resetTimelineSearchUiState();
            this.loadChatActivity();
          }
          this.loadEntryShares();
        } else {
          // No patient selected, clear data
          this.patient = null;
          this.timelineData = null;
          this.filteredTimelineData = null;
          this.sendingDetails = { name: '', email: '' };
          this.resetTimelineSearchUiState();
        }
      });

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedGroup => {
        if (selectedGroup) {
          this.isSurgicalSpeciality = checkIfGroupIsSurgical(selectedGroup.groupSpeciality);
        }
      });

    this.userProfile = this.userService.user;
    /* this.patientAdmissionHistoryService.patientWardChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (wc) => {
          this.wardChanges = this.filterWardChanges(wc);
        },
        error: (err) => {
          this.wardChanges = [];  // Optionally reset or handle errors as needed
        }
      }); */
  }

  ngOnInit(): void {
    this.watchChatActivity();
    this.loadChatActivity();
    this.loadTimelineData();
    this.watchDocumentGalleryExpiry();
    this.loadEntryShares();

    this.isPhysician = this.userService.isUserPhysician;
    this.canSign = this.userService.canUserSign;
    this.hasSignaturePermission = this.userService.isNonPhysician;
    this.groupUsers = this.userService.availableDoctorsList;

    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((results: any[] | null) => {
        const uid = this.userService.user?.id;
        if (!results?.length || !uid) {
          return;
        }
        const row = results.find((u: any) => u.userId === uid);
        this.canDownloadRecordings = row?.canDownloadRecordings !== false;
      });
  }


  /* ── sharing ONE entry with a referral link (architecture §21) ─────────────────── */

  get referralsEnabled(): boolean {
    return referralsEnabledFor(this.groupService.group);
  }

  /** A state unit (tasks/state-mode.md) sends nothing from the timeline: no email, WhatsApp, app or referral shares. */
  get stateUnit(): boolean {
    return isStateModeGroup(this.groupService.group);
  }
  /**
   * Links that can receive a CHOSEN ENTRY right now; nothing is offered when there are none. §25: that is another
   * MedicAI practice. A practice whose network is all outside clinicians sees no share icon, because there is nowhere
   * for one entry of the patient file to go - they refer, with a referral note.
   */
  activeReferralLinks = 0;
  /** `sourceType|sourceId` -> how many links already have it, from the share ledger. */
  private entryShares = new Map<string, number>();
  /** The patient those counts belong to, and when they were last read. */
  private entrySharesFor: string | null = null;
  private entrySharesReadAt = 0;

  /**
   * What the server would share of this item, or null when it would share nothing of it. A note filed FROM a referral is
   * never sent back where it came from (D23), and a WhatsApp thread is not part of the record a referral carries.
   */
  shareSource(item: any): { sourceType: ShareableSourceType; sourceId: string } | null {
    if (!item?.id || item.externalSource) return null;
    if (item.type === 'note' && !item.newWard) return { sourceType: 'note', sourceId: item.id };
    if (item.type === 'admission-record') return { sourceType: 'admission', sourceId: item.id };
    if (item.type === TIMELINE_DOCUMENT_TYPE) return { sourceType: 'document', sourceId: item.id };
    return null;
  }

  canShareItem(item: any): boolean {
    return this.referralsEnabled && this.activeReferralLinks > 0 && !!this.shareSource(item);
  }

  /** How many links already have this item. Zero shows no badge: a counter of nothing is noise. */
  shareCount(item: any): number {
    const source = this.shareSource(item);
    return source ? this.entryShares.get(entryShareKey(source.sourceType, source.sourceId)) || 0 : 0;
  }

  shareTooltip(item: any): string {
    const count = this.shareCount(item);
    if (!count) return 'Share this entry with a referral link';
    return `Shared with ${count} referral link${count === 1 ? '' : 's'} · share with another`;
  }

  /**
   * Who can receive something, and what each entry of this patient has already reached.
   *
   * The counters are the DELIVERY ledger, so they are not only about items somebody picked: a referral of the whole
   * file counts on every entry it sends, and an entry written later counts as soon as the window carries it. That
   * happens in a share job seconds or minutes after the referral, so the counts are read again when the timeline moves.
   */
  private loadEntryShares(force = false): void {
    const patientId = this.patient?.id;
    if (!this.referralsEnabled || !patientId) return;
    if (!force && this.entrySharesFor === patientId) return;
    if (this.entrySharesFor !== patientId) {
      this.entryShares = new Map();
      this.entrySharesFor = patientId;
    }
    this.entrySharesReadAt = Date.now();

    this.referralService.listRecipients().pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ options }) => {
        // Practice links take entries freely; a clinician outside MedicAI within each referral's document allowance (2026-09-29).
        this.activeReferralLinks = options.filter((o) => o.status === 'active' && ((o.shareModes ?? []).includes('items') || (o.shareModes ?? []).includes('summary'))).length;
        this.cdr.markForCheck();
      },
      error: () => { this.activeReferralLinks = 0; },
    });
    this.referralService.listEntryShareCounts(patientId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (counts) => {
        if (this.patient?.id !== patientId) return;
        this.entryShares = new Map(counts.map((c) => [entryShareKey(c.sourceType, c.sourceId), c.links]));
        this.cdr.markForCheck();
      },
      error: () => { this.entryShares = new Map(); },
    });
  }

  /** The timeline redrew: pick up deliveries the share job has made since, without asking on every redraw. */
  private refreshEntrySharesSoon(): void {
    if (Date.now() - this.entrySharesReadAt < 15000) return;
    this.loadEntryShares(true);
  }

  /* ── the patient's own app (tasks/patient-app.md §6.1) ───────────────────────────────────────────── */

  /** Whether this patient's file is linked to the patient app, and what of it is there already. */
  private patientAppLinked = false;
  private patientAppShares = new Set<string>();
  private patientAppFor: string | null = null;

  private loadPatientAppShares(force = false): void {
    const patientId = this.patient?.id;
    if (!patientId || (!force && this.patientAppFor === patientId)) return;
    this.patientAppFor = patientId;
    this.patientAppLinked = false;
    this.patientAppShares = new Set();
    this.patientAppService.listShares(patientId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (state) => {
        if (this.patient?.id !== patientId) return;
        this.patientAppLinked = state.linked && state.canShare;
        this.patientAppShares = new Set(state.shares.filter((s) => !s.withdrawnAt).map((s) => `${s.sourceType}|${s.sourceId}`));
        this.cdr.markForCheck();
      },
      error: () => { this.patientAppLinked = false; },
    });
  }

  /** What the patient's app can receive of this item: a note, a validated document, a patient-friendly discharge summary. */
  patientAppSource(item: any): { sourceType: PatientAppSourceType; sourceId: string } | null {
    this.loadPatientAppShares();
    if (!this.patientAppLinked || this.stateUnit || !item?.id || item.externalSource) return null;
    if (item.type === 'note' && !item.newWard) return { sourceType: 'note', sourceId: item.id };
    if (item.type === TIMELINE_DOCUMENT_TYPE) return { sourceType: 'document', sourceId: item.id };
    if (item.type === 'admission-record' && item.patientDischargeSummary) return { sourceType: 'discharge_summary', sourceId: item.id };
    return null;
  }

  patientAppShared(item: any): boolean {
    const source = this.patientAppSource(item);
    return !!source && this.patientAppShares.has(`${source.sourceType}|${String(source.sourceId).toLowerCase()}`);
  }

  patientAppTooltip(item: any): string {
    return this.patientAppShared(item) ? "In the patient's app · withdraw or check" : "Send to the patient's app";
  }

  openPatientAppShare(event: Event, item: any): void {
    event.stopPropagation();
    const source = this.patientAppSource(item);
    if (!source || !this.patient?.id) return;
    const ref = this.dialog.open<PatientAppShareDialogComponent, PatientAppShareDialogData, PatientAppShareDialogResult>(PatientAppShareDialogComponent, {
      width: '520px', maxHeight: '90vh', autoFocus: false,
      data: {
        patientId: this.patient.id,
        patientName: `${this.patient?.name || ''} ${this.patient?.surname || ''}`.trim(),
        entryTitle: source.sourceType === 'discharge_summary' ? 'Patient-friendly discharge summary' : (item.title || item.documentName || 'This entry'),
        sourceType: source.sourceType,
        sourceId: source.sourceId,
      },
    });
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result?.changed) this.loadPatientAppShares(true);
    });
  }

  openShareEntryDialog(event: Event, item: any): void {
    event.stopPropagation();
    const source = this.shareSource(item);
    if (!source || !this.patient?.id) return;
    const ref = this.dialog.open<ShareEntryDialogComponent, ShareEntryDialogData, ShareEntryDialogResult>(ShareEntryDialogComponent, {
      width: '560px',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        patientId: this.patient.id,
        patientName: `${this.patient?.name || ''} ${this.patient?.surname || ''}`.trim(),
        entryTitle: item.title || item.documentName || 'This entry',
        sourceType: source.sourceType,
        sourceId: source.sourceId,
      },
    });
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result?.shared) this.loadEntryShares(true);
    });
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
  }

  /* ═════════════════════════════════════════════════════════════════════════════
   * THE RETURNED PAGES — fetched per row, cached by row, and re-fetched before their links die
   *
   * WHY THIS IS NOT PART OF THE TIMELINE'S OWN LOAD. `patientDocumentRequests$` is the LIST
   * endpoint, and the list serves `returnedFileCount` and no files: the pages come from
   * `GET /patient-document-requests/:id`, one row at a time, which is the same call the documents
   * panel makes when a return is expanded. So the gallery cannot be built in
   * `combineAndSortTimelineData` — it is filled in afterwards and the rows redraw.
   *
   * WHY THE CACHE IS KEYED BY REQUEST ID AND NOT REBUILT WITH THE TIMELINE. That combineLatest
   * re-emits whenever notes, admissions or WhatsApp move, and rebuilding the rows must not re-fire
   * a read per document every time somebody writes a note. The cache outlives the rows; the rows
   * borrow from it.
   *
   * WHY THERE IS A SWEEP AT ALL. Fifteen minutes (`DEFAULT_SAS_MINUTES`). A note's media carries a
   * ten-year token and can sit in a list indefinitely; these links cannot, and this gallery —
   * unlike the panel's — is not something a user opened seconds ago. A patient file left open on a
   * second screen through a consultation would otherwise fill with broken pages.
   * ═════════════════════════════════════════════════════════════════════════════ */

  /** Request id → its pages and their expiry. Survives every timeline rebuild. */
  private documentGalleries = new Map<string, TimelineDocumentGallery>();
  /** Reads in flight, so a rebuild mid-read does not fire a second one. */
  private documentGalleryLoading = new Set<string>();
  /** §26. The clinician's observations, keyed the same way and for the same reason. */
  private documentNotes = new Map<string, string>();

  /**
   * Start the expiry sweep. Called once, from `ngOnInit`.
   *
   * It only ever re-reads rows that are ON SCREEN (`displayedTimelineData`): the timeline renders
   * in batches and a patient with two years of returned questionnaires must not have every one of
   * them re-minting links nobody is looking at.
   */
  private watchDocumentGalleryExpiry(): void {
    interval(TIMELINE_DOCUMENT_GALLERY_SWEEP_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.expanded) return;
        const now = Date.now();
        for (const item of this.documentRowsOnScreen()) {
          const cached = this.documentGalleries.get(String(item.id));
          if (!cached) continue;
          if (documentGalleryIsStale(cached.expiresAt, now, TIMELINE_DOCUMENT_GALLERY_GRACE_MS)) {
            this.documentGalleries.delete(String(item.id));
            this.loadDocumentGallery(String(item.id));
          }
        }
      });
  }

  /** The document rows the user can actually see, across the day groups currently rendered. */
  private documentRowsOnScreen(): any[] {
    const rows: any[] = [];
    for (const group of this.displayedTimelineData || []) {
      for (const item of (group && group.items) || []) {
        if (item && item.type === TIMELINE_DOCUMENT_TYPE && item.id) rows.push(item);
      }
    }
    return rows;
  }

  /**
   * Give every visible document row its pages, reading only what is missing.
   *
   * Called after each render pass. A row already in the cache is served from it; a row being read
   * is left alone; everything else is fetched once.
   */
  private hydrateDocumentGalleries(): void {
    for (const item of this.documentRowsOnScreen()) {
      const id = String(item.id);
      const cached = this.documentGalleries.get(id);
      if (cached) {
        item.galleryFiles = cached.files;
        item.galleryExpiresAt = cached.expiresAt;
        item.validationNote = this.documentNotes.get(id) || null;
        item.media = cached.media;
        item.returnedMedia = cached.returnedMedia;
        item.validationMedia = cached.validationMedia;
        continue;
      }
      this.loadDocumentGallery(id);
    }
  }

  /**
   * One row's returned pages.
   *
   * A FAILED READ IS LEFT AS NO GALLERY, not as an error on the row. The timeline is a reading
   * surface and this is one row among many; the documents panel is where a reviewer goes when
   * something is wrong with a return, and it reports its own failures loudly. What must not happen
   * is a half-drawn gallery claiming to be the record.
   */
  private loadDocumentGallery(id: string): void {
    if (!id || this.documentGalleryLoading.has(id) || this.documentGalleries.has(id)) return;
    this.documentGalleryLoading.add(id);

    /*
     * §32 — the practice this read is about. The timeline only ever draws ONE patient's file, so
     * the open patient's practice is the right answer by construction, and it is the same value
     * the row itself carries.
     */
    this.patientDocumentRequestService.loadRequestById(id, this.patient?.groupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          this.documentGalleryLoading.delete(id);
          const detail = result && result.data ? result.data : null;
          const gallery = documentGallery(detail);
          this.documentGalleries.set(id, gallery);
          // §26. The observations ride the SAME read as the pages, because the list endpoint
          // strips them — they are encrypted clinical prose and `toStaffDto` keeps them off every
          // list response. Cached beside the gallery so a timeline rebuild does not lose them.
          if (detail && detail.validationNote) this.documentNotes.set(id, detail.validationNote);
          for (const item of this.documentRowsOnScreen()) {
            if (String(item.id) !== id) continue;
            item.galleryFiles = gallery.files;
            item.galleryExpiresAt = gallery.expiresAt;
            item.validationNote = this.documentNotes.get(id) || null;
            // §26. THE SAME PAGES under the name the rest of the timeline reads — `downloadAsPdf`,
            // `sendTimelineRecord` and the WhatsApp trigger all reach for `item.media`. See the
            // gallery's own note on why a `FileObject` cannot be handed to them directly.
            item.media = gallery.media;
            // §A — the same pages kept apart, for the cover-page export. See the gallery.
            item.returnedMedia = gallery.returnedMedia;
            item.validationMedia = gallery.validationMedia;
          }
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          this.documentGalleryLoading.delete(id);
          console.error('Error loading returned document pages for timeline row: ', error);
        }
      });
  }

  /**
   * THE FIFTH SOURCE — the practice's chat about this patient (messaging hub, tasks/messaging-hub-architecture.md §7).
   * A per-day count of the chat AS FILED ON THIS PATIENT'S RECORD (M2b: accepted entries in PatientChatEntries), so it
   * shows even when the hub is down. Reloaded when the patient changes and when the backend files a new entry for them
   * (socket `patientChatUpdated`). Empty (nothing drawn) when chat is off or nothing is filed.
   */
  private readonly chatActivity$ = new BehaviorSubject<PatientChatActivity>({ conversationId: null, days: [] });
  private chatActivityFor: string | null = null;
  private chatActivityReload: ReturnType<typeof setTimeout> | null = null;

  private loadChatActivity(): void {
    const patient = this.patient;
    if (!patient?.id) return;
    this.chatActivityFor = patient.id;
    this.chatActivity$.next({ conversationId: null, days: [] });
    void this.chatService.recordedChatDays(patient.id).then((activity) => {
      if (this.patient?.id === patient.id) this.chatActivity$.next(activity);
    });
  }

  /** "3 messages in the practice chat · 2 in referral chats" (M6: referral chats are counted apart). */
  chatDayLine(item: { messageCount: number; referralCount?: number }): string {
    const referral = item.referralCount ?? 0;
    const practice = item.messageCount - referral;
    const plural = (n: number) => (n === 1 ? '' : 's');
    const parts: string[] = [];
    if (practice > 0) parts.push(`${practice} message${plural(practice)} in the practice chat`);
    if (referral > 0) parts.push(practice > 0 ? `${referral} in referral chats` : `${referral} message${plural(referral)} in referral chats`);
    return parts.join(' · ');
  }

  /** The backend filed a chat entry for this patient: fetch the counts again (debounced; a burst is one reload). */
  private watchChatActivity(): void {
    this.socketService.listen('patientChatUpdated', 'patient-clinical-timeline')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: { patientId?: string }) => {
        if (!data?.patientId || !this.patient?.id || String(data.patientId).toLowerCase() !== String(this.patient.id).toLowerCase()) return;
        if (this.chatActivityReload) clearTimeout(this.chatActivityReload);
        this.chatActivityReload = setTimeout(() => this.loadChatActivity(), 300);
      });
  }

  openPatientChatFromTimeline(): void {
    if (!this.patient) return;
    void this.chatService.openPatientChat(this.patient).catch(() => undefined);
  }

  loadTimelineData(): void {
    combineLatest({
      notes: this.patientNoteService.patientNotes$,
      ah: this.patientAdmissionHistoryService.patientAdmissionRecords$,
      whatsappMessages: this.whatsappService.messages$,
      // THE FOURTH SOURCE — tasks/practice-documents.md §6 item 3. It emits `null` while the
      // patient's requests are still loading, which `combineLatest` cannot skip, so the mapping
      // treats null as "no documents yet"; the timeline redraws when the real list arrives.
      documentRequests: this.patientDocumentRequestService.patientDocumentRequests$,
      chatActivity: this.chatActivity$,
    }).pipe(
      map(({ notes, ah, whatsappMessages, documentRequests, chatActivity }) => {
        return this.combineAndSortTimelineData(
          notes || [], ah || [], whatsappMessages || [], documentRequests || [], chatActivity);
      }),
      catchError((err) => {
        console.error('Error loading timeline data:', err);
        // Return empty array on error
        return of([]);
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (combinedData) => {
        // Only update if we have a current patient to prevent stale data
        if (this.patient) {
          this.timelineData = combinedData;
          this.applyFilters();
          this.refreshEntrySharesSoon();
        }
      },
      error: (err) => {
        console.error('Error in timeline data subscription:', err);
        this.timelineData = [];
        this.filteredTimelineData = [];
        this.displayedTimelineData = [];
        this.hasMoreItems = false;
      }
    });
  }

  combineAndSortTimelineData(
    notes: any,
    admissionHistory: any,
    whatsappMessages: any[],
    documentRequests: any[] = [],
    chatActivity: PatientChatActivity = { conversationId: null, days: [] }
  ): any[] {
    // Group WhatsApp messages by date (only outbound messages)
    const whatsappByDate = _.groupBy(
      whatsappMessages.filter((msg: any) => msg.direction === 'outbound' && msg.patientId === this.patient?.id),
      (msg: any) => moment(msg.createdAt).format('YYYY-MM-DD')
    );

    // Create WhatsApp communication items for each unique date
    const whatsappItems = Object.keys(whatsappByDate).map(date => {
      const messages = whatsappByDate[date];
      // Count unique contacts (phone numbers) that messages were sent to
      const uniqueContacts = new Set(messages.map((msg: any) => msg.to));
      return {
        type: 'whatsapp-communication',
        date: date,
        createdAt: messages[0].createdAt, // Use first message's createdAt for sorting
        messageCount: messages.length,
        contactCount: uniqueContacts.size,
        messages: messages
      };
    });

    // Add processing status flag to notes
    const notesWithProcessing = notes.map((note: any) => ({
      ...note,
      type: 'note',
      hasProcessingPdf: this.checkHasProcessingPdf(note.media)
    }));

    // §6 item 3. Only a request with something RETURNED against it right now — the filtering and
    // the score rendering are `clinical-timeline-documents.ts`, deliberately, so neither depends
    // on this component being instantiable.
    const documentItems = toTimelineDocuments(documentRequests as any, this.patient?.id);

    // The patient chat: one row per day it was used, like WhatsApp's.
    const chatItems = chatActivity.days.map((d) => ({
      type: 'patient-chat',
      date: d.date,
      createdAt: d.lastAt,
      messageCount: d.count,
      referralCount: d.referralCount ?? 0,
      conversationId: chatActivity.conversationId,
    }));

    const combined = [
      ...notesWithProcessing,
      ...chatItems,
      ...admissionHistory.map((ah: any) => ({ ...ah, date: ah.dischargeDate, type: 'admission-record' })),
      ...whatsappItems,
      ...documentItems,
    ];

    // Group notes by formatted date 
    /* TODO: the notes here dont have a time component so are always pushed above an ah fro the same day */
    const grouped = _.groupBy(combined, item => item.date ? moment(item.date).format('YYYY-MM-DD') : moment(item.createdAt).format('YYYY-MM-DD'));

    // Determine if the group date falls within any admission record period or part of the current admission
    const isInpatient = (date: any) => {
      if (this.patient.status === 'inpatient') {
        return moment(date).isSameOrAfter(moment(this.patient.admissionDate, 'DD/MM/YYYY'), 'day') || admissionHistory.some((ah: any) => moment(date).isBetween(moment(ah.admissionDate), moment(ah.dischargeDate), 'day', '[]'))
      } else {
        return admissionHistory.some((ah: any) => moment(date).isBetween(moment(ah.admissionDate), moment(ah.dischargeDate), 'day', '[]'))
      }
    };

    // Sort the groups by date in descending order and transform into an array
    return _.chain(grouped)
      .map((value, key) => ({ date: key, isInpatient: isInpatient(key), items: _.orderBy(value, item => moment(item.createdAt).format('YYYY-MM-DD'), 'asc') }))
      // Use the ISO date string directly for sorting
      .orderBy(group => group.date, 'desc')
      .value();

  }

  /* filterWardChanges(changes: any[] | null) {
    return changes?.filter(wc => {
      const date = moment.utc(wc.date).startOf('day');
      const admissionDate = moment.utc(this.editingAdmissionRecord.admissionDate).startOf('day');
      const dischargeDate = moment.utc(this.editingAdmissionRecord.dischargeDate).endOf('day');
      return date.isSameOrAfter(admissionDate, 'day') && date.isSameOrBefore(dischargeDate, 'day')
    });
  } */


  getAuthNumbersList(authNumbers: any[]): string {
    // Sort the authorization numbers in descending order by date
    const sortedAuthNumbers = _.orderBy(authNumbers, [(auth) => moment(auth.date)], ['desc']);
    // Map the sorted list to the desired format
    return sortedAuthNumbers.map(a => `${moment(a.date).format('DD/MM/YYYY')}: ${a.authNumber}`).join(', ');
  }

  isDate(value: any): boolean {
    return !isNaN(Date.parse(value));
  }

  copyNote(note: any): void {
    this.clipboardService.copyMarkdown(note.notes).then(ok => {
      this.showToastMessage(ok ? 'Note copied to clipboard' : 'Failed to copy to your clipboard');
    });
  }

  copyAdmissionRecord(item: any): void {
    const text = this.compileAdmissionRecord(item);
    this.clipboardService.copyMarkdown(text).then(ok => {
      this.showToastMessage(ok ? 'Admission record copied to clipboard' : 'Failed to copy to your clipboard');
    });
  }

  /** A note filed from outside (a referral reply, a patient's entry): read-only, but it can be deleted like any note. */
  async deleteExternalNote(note: any): Promise<void> {
    if (!note?.id) return;
    const confirmed = await this.showConfirmationDialog('Delete this entry?', 'It is taken off the patient timeline, as a deleted note is.', undefined, 'Cancel', 'Delete');
    if (!confirmed) return;
    this.patientNoteService.deleteNote(note.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.showToastMessage('Entry deleted'),
      error: () => this.showToastMessage('The entry could not be deleted. Please try again.'),
    });
  }

  loadNote(note: any) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    this.patientNoteService.setEditingNote(note);
    this.router.navigate(['/patients', {
      outlets: {
        patient: [(note.isMorbidity || note.isMortality) ? 'edit-mm' : 'edit-note', note.id],
      }
    }]);

  }

  loadAdmissionRecord(ah: any) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    this.patientAdmissionHistoryService.setEditingAH(ah);
    this.router.navigate(['/patients', {
      outlets: {
        patient: ['edit-admission-record', ah.id],
      }
    }]);

  }

  sortNoteHistory(history: any[]) {
    return _.orderBy(history, (h) => {
      return moment(h.createdAt);
    }).reverse();
  }

  showNoteVersion(event: MouseEvent, version: any) {
    event.preventDefault();
    event.stopPropagation();
    this.showConfirmationDialog(`${moment(version.date).format('DD/MM/YYYY')}`, `${version.title}\n\n${version.notes}`, undefined, 'OK', '')
  }

  /**
   * Safely format a date value using moment, handling various input formats
   * Public method for use in templates
   */
  safeFormatDate(dateValue: any, format: string = 'DD/MM/YYYY'): string {
    if (!dateValue) {
      return 'N/A';
    }

    try {
      // Try to parse with moment - it handles Date objects, ISO strings, and various formats
      const dateMoment = moment(dateValue);
      if (dateMoment.isValid()) {
        return dateMoment.format(format);
      }
      // If moment can't parse it, try DatePipe as fallback
      const dateObj = dateValue instanceof Date ? dateValue : new Date(dateValue);
      if (!isNaN(dateObj.getTime())) {
        return this.datePipe.transform(dateObj, format) || 'N/A';
      }
    } catch (error) {
      console.warn('Error formatting date:', dateValue, error);
    }

    return 'N/A';
  }

  /**
   * Safely format a time value using moment or DatePipe
   */
  private safeFormatTime(dateValue: any, format: string): string {
    if (!dateValue) {
      return 'N/A';
    }

    try {
      const dateMoment = moment(dateValue);
      if (dateMoment.isValid()) {
        return dateMoment.format(format);
      }
      const dateObj = dateValue instanceof Date ? dateValue : new Date(dateValue);
      if (!isNaN(dateObj.getTime())) {
        return this.datePipe.transform(dateObj, format) || 'N/A';
      }
    } catch (error) {
      console.warn('Error formatting time:', dateValue, error);
    }

    return 'N/A';
  }

  compileAdmissionRecord(item: any) {

    let content = 'Admission Record\n\n';

    const admissionDate = this.safeFormatDate(item.admissionDate, 'DD/MM/YYYY');
    const dischargeDate = this.safeFormatDate(item.dischargeDate, 'DD/MM/YYYY');
    const dateDiff = (item.admissionDate && item.dischargeDate)
      ? this.dateDiffPipe.transform(item.admissionDate, item.dischargeDate)
      : 'N/A';

    content += `${admissionDate} to ${dischargeDate} (${dateDiff})\n\n`;

    content += `Admitted By: ${item.admittedBy || 'N/A'}\n`;
    content += `Discharged By: ${item.dischargedBy || 'N/A'}\n\n`;

    content += `Primary Diagnosis: ${item.diagnosis || 'N/A'}\n`;
    const diagnosisArray = this.getDiagnosisArray(item);
    for (let i = 0; i < diagnosisArray.length; i++) {
      if (i === 0) {
        content += `Additional Diagnosis: ${diagnosisArray[i]}\n`;
      } else {
        content += `${diagnosisArray[i]}\n`;
      }
    }
    if (diagnosisArray.length) {
      content += '\n';
    }

    content += `Admission Summary: ${item.admissionType || 'N/A'}${item.admissionSummary ? ' - ' + item.admissionSummary : ''}\n\n`;

    content += `${item.hospital || 'N/A'}${item.ward ? ' (' + item.ward + ')' : ''}\n\n`;

    content += `${item.dischargeSummary || ''}\n\n`;

    const dischargedAt = this.safeFormatTime(item.createdAt, 'HH:mm');
    content += `Discharged At: ${dischargedAt}`;

    return content;
  }

  onRecipientSelected(recipient: { name: string, email: string }) {
    this.sendingDetails.name = recipient.name;
    this.sendingDetails.email = recipient.email;
  }

  async sendTimelineRecord(item: any, type: 'note' | 'admission-record' | 'document-return') {
    const recordType = this.recordTypeLabel(type);
    const rawContent = this.recordContent(item, type);

    // Strip HTML tags to convert to plain text
    const plainTextContent = this.pdfDownloadService.convertToPlainText(rawContent);

    if (!this.sendingDetails.email) {
      this.showToastMessage('An email address was not provided. Please check the relevant email field try again.');
      return;
    }

    const grid = this.embedMediaAsGrid;
    const pdfDate = item.pdfDateOverride ?? item.date;

    /*
     * The three branches only ever chose a SIGNATURE and then repeated the same eleven-argument
     * call. Resolved once here so the pre-rendered PDF below cannot be attached on one branch and
     * forgotten on another — which is precisely how a download and its email drift apart.
     *
     * `null` preserves an existing gap exactly: a non-physician WITHOUT signing permission who has
     * the signature toggle on sends nothing today. That is very likely a bug, but it is not this
     * change's bug, and quietly starting to send those emails would be a surprise.
     */
    const signature = this.resolveEmailSignature();
    if (!signature) return;

    const images = item.media?.filter((media: any) => media.mediaType !== 'application/pdf');

    /*
     * §3.3 — THE EMAILED COPY IS THE DOWNLOADED FILE.
     *
     * For a returned document the client draws the PDF here and posts the BYTES. Every other record
     * type still posts text for the server to render, exactly as before.
     *
     * This is not a nicety. The cover page is drawn by jsPDF from `returnedMedia`/`validationMedia`
     * and the server's pdf-lib renderer has no way to reproduce it, so without this the email
     * carries a document that looks plausible and is not the one the clinician read and sent.
     *
     * A render failure sends NOTHING rather than falling back to the server's version: silently
     * emailing a different document is the outcome this entire seam exists to prevent.
     */
    let preRenderedPdf: string | undefined;
    if (type === 'document-return') {
      try {
        preRenderedPdf = await this.pdfDownloadService.generatePdfAsBase64({
          content: plainTextContent,
          filename: this.recordFilename(recordType),
          patient: this.patient,
          images,
          attachSignature: signature.attachSignature,
          signatureUrl: signature.signatureUrl,
          signatoryName: signature.signatoryName,
          date: pdfDate,
          embedMediaAsGrid: grid,
          coverPage: this.coverPageFor(item, type),
        });
      } catch (err) {
        console.error('Could not render the returned document for emailing:', err);
        preRenderedPdf = undefined;
      }
      if (!preRenderedPdf) {
        this.showToastMessage(
          'Could not prepare the document for emailing. Please try again or contact support.');
        return;
      }
    }

    this.emailCommunication({
      patientId: item.patientId,
      type: recordType,
      name: this.sendingDetails.name,
      email: this.sendingDetails.email,
      content: plainTextContent,
      images,
      attachSignature: signature.attachSignature,
      signatureUrl: signature.signatureUrl,
      signatoryName: signature.signatoryName,
      embedMediaAsGrid: grid,
      pdfDate,
      preRenderedPdf,
    });
  }

  /** Which signature this email carries, or `null` for "send nothing" — see `sendTimelineRecord`. */
  private resolveEmailSignature():
    { attachSignature?: boolean; signatureUrl?: string; signatoryName?: string } | null {
    if (this.includeSignatureOnEmail && this.isPhysician) {
      return {
        attachSignature: true,
        signatureUrl: this.userProfile.signatureImageUrl,
        signatoryName:
          `${this.userProfile.title} ${this.userProfile.name} ${this.userProfile.surname}`,
      };
    }
    if (!this.isPhysician && this.hasSignaturePermission && this.includeSignatureOnEmail) {
      return {
        attachSignature: true,
        signatureUrl: this.selectedDoctor.signatureImageUrl,
        signatoryName:
          `${this.selectedDoctor.title} ${this.selectedDoctor.name} ${this.selectedDoctor.surname}`,
      };
    }
    if (!this.includeSignatureOnEmail) {
      return { attachSignature: undefined, signatureUrl: undefined, signatoryName: undefined };
    }
    return null;
  }



  /* ═════════════════════════════════════════════════════════════════════════════
   * WHAT A TIMELINE RECORD IS CALLED, AND WHAT IT SAYS — the two lines every action needs
   *
   * `downloadAsPdf` and `sendTimelineRecord` were each carrying their own copy of these as
   * ternaries. A third record type would have meant a third ternary in each, which is how the two
   * paths end up disagreeing about what a document is called in a filename versus in an email
   * subject. One pair of helpers, used by both.
   * ════════════════════════════════════════════════════════════════════════════ */

  private recordTypeLabel(type: 'note' | 'admission-record' | 'document-return'): string {
    if (type === 'note') return 'Note';
    if (type === 'admission-record') return 'Admission Record';
    return 'Returned Document';
  }

  private recordContent(item: any, type: 'note' | 'admission-record' | 'document-return'): string {
    if (type === 'note') return item.notes;
    if (type === 'admission-record') return this.compileAdmissionRecord(item);
    // The clinician's observation follows the cover page. Unticking "Include cover page" takes it
    // out of the PDF, and it has to leave the email body with it - see `compileDocumentReturn`.
    return compileDocumentReturn(item, { includeValidationNote: this.includeCoverPage });
  }

  /** §26. The row's own text, on the clipboard. */
  copyDocumentReturn(item: any): void {
    this.clipboardService.copyMarkdown(compileDocumentReturn(item)).then((ok) => {
      this.showToastMessage(ok
        ? 'Returned document copied to clipboard'
        : 'Failed to copy to your clipboard');
    });
  }

  /**
   * The pages a PDF can actually embed.
   *
   * `mapMediaToFileObject` filters PDFs out of every gallery, and the PDF embedder can only draw
   * raster images. Returned PDFs are converted to page images server-side on upload, so in practice
   * nothing is lost — but a page that somehow arrives as a PDF alone would be SILENTLY absent from
   * the export, so the caller is warned rather than left to discover a short document.
   */
  /**
   * §A of `tasks/pdf-letterhead-placement.md` — a RETURNED DOCUMENT is not laid out like a note.
   *
   * It gets a cover page carrying the letterhead, the demographics and the clinician's observations
   * and attachments, and then the patient's own returned pages one per page at full size with no
   * chrome at all. The practice's reading of the document is the covering note; the document is the
   * document.
   *
   * Shared by the download and the email ON PURPOSE — they must produce the same bytes, and the
   * surest way to guarantee that is to give them one description of the document to build.
   */
  private coverPageFor(item: any, type: 'note' | 'admission-record' | 'document-return') {
    if (type !== 'document-return') return undefined;
    return {
      coverImages: this.pdfSafeMedia(item.validationMedia),
      fullPageImages: this.pdfSafeMedia(item.returnedMedia),
      // The toggle rides HERE, on the one description both the download and the email build from,
      // for the same reason the rest of it does: a toggle honoured on one path and not the other
      // is how "the emailed copy is the downloaded file" (§3.3) quietly stops being true.
      omitCover: !this.includeCoverPage,
    };
  }

  /**
   * Whether "Include cover page" can be unticked on this row at all.
   *
   * A returned document with no embeddable page - every page arrived as a PDF the server could
   * not convert, or the row carries none - has nothing to export WITHOUT the cover, so offering
   * the choice would offer a file that cannot be built. The service refuses that case anyway;
   * this is so the clinician never reaches the refusal.
   */
  canDropCoverPage(item: any): boolean {
    // NOT `pdfSafeMedia`: this is bound in the template and runs on every change detection pass,
    // and that helper warns to the console for each page it drops.
    const rows = Array.isArray(item?.returnedMedia) ? item.returnedMedia : [];
    return rows.some((row: any) => row && row.mediaUrl && row.mediaType !== 'application/pdf');
  }

  /** Shared with the email path, for the same reason `coverPageFor` is. */
  private recordFilename(recordType: string): string {
    return `MedicAI_${this.patient.name} ${this.patient.surname}_${recordType}_${moment().valueOf()}`;
  }

  private pdfSafeMedia(media: any[] | undefined): any[] {
    const rows = Array.isArray(media) ? media : [];
    const usable = rows.filter((row) => row && row.mediaUrl
      && row.mediaType !== 'application/pdf');
    if (usable.length !== rows.length) {
      console.warn('[timeline] %d returned page(s) could not be embedded in the PDF',
        rows.length - usable.length);
    }
    return usable;
  }

  getDiagnosisArray(patient: any): string[] {
    const diagnosisArray = [];
    for (let i = 2; i <= 24; i++) {
      const fieldName = `diagnosis${i}`;
      if (patient[fieldName]) {
        diagnosisArray.push(patient[fieldName]);
      }
    }
    return diagnosisArray;
  }

  async downloadAsPdf(
    event: MouseEvent, item: any, type: 'note' | 'admission-record' | 'document-return',
  ) {
    event.preventDefault();
    event.stopPropagation();

    const recordType = this.recordTypeLabel(type);
    const content = this.recordContent(item, type);
    const date = item.pdfDateOverride ?? item.date;

    const filename = this.recordFilename(recordType);

    const mediaForPdf = item.media?.filter((media: any) => media.mediaType !== 'application/pdf');

    const coverPage = this.coverPageFor(item, type);

    try {
      if (this.includeSignatureOnPdf && this.isPhysician) {
        const appendSignatureMessage = content;
        await this.pdfDownloadService.downloadPdf({
          content: appendSignatureMessage,
          filename: filename,
          patient: this.patient,
          images: mediaForPdf,
          attachSignature: this.includeSignatureOnPdf,
          signatureUrl: this.userProfile.signatureImageUrl,
          signatoryName: `${this.userProfile.title} ${this.userProfile.name} ${this.userProfile.surname}`,
          date: date,
          embedMediaAsGrid: this.embedMediaAsGrid,
          coverPage,
        });
      } else if (!this.isPhysician && this.hasSignaturePermission) {
        const appendSignatureMessage = content;
        await this.pdfDownloadService.downloadPdf({
          content: appendSignatureMessage,
          filename: filename,
          patient: this.patient,
          images: mediaForPdf,
          attachSignature: this.includeSignatureOnPdf,
          signatureUrl: this.selectedDoctor.signatureImageUrl,
          signatoryName: `${this.selectedDoctor.title} ${this.selectedDoctor.name} ${this.selectedDoctor.surname}`,
          date: date,
          embedMediaAsGrid: this.embedMediaAsGrid,
          coverPage,
        });
      } else if (!this.includeSignatureOnPdf || !this.canSign) {
        await this.pdfDownloadService.downloadPdf({
          content: content,
          filename: filename,
          patient: this.patient,
          images: mediaForPdf,
          attachSignature: false,
          date: date,
          embedMediaAsGrid: this.embedMediaAsGrid,
          coverPage,
        });
      }
    } catch (err) {
      console.error('PDF download failed:', err);
      this.showToastMessage('Could not generate PDF. If the note includes images, try again or contact support.');
    }

    this.closeAllMenus(this.menuTriggers);

  }

  /**
   * Download the stitched encounter audio for a note that has an associated recording job.
   * Gated on `canDownloadRecordings`; only one download runs at a time.
   */
  async downloadEncounterAudio(event: MouseEvent, item: any): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    if (!item?.recordingJobId || !this.canDownloadRecordings || this.downloadingAudioId) {
      return;
    }

    const patientId = item.patientId || this.patient?.id;
    if (!patientId) {
      return;
    }

    this.downloadingAudioId = item.id;

    try {
      const token = localStorage.getItem('token');
      const url = `${environment.API_URL}/api/recordings/${patientId}/${item.recordingJobId}/job-download`;
      const blob = await firstValueFrom(this.http.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      }));

      await this.fileDownloadService.download(blob, this.buildEncounterAudioFileName(item));
    } catch (err: any) {
      console.error('Encounter audio download failed:', err);
      if (err?.status === 404) {
        this.showToastMessage('No audio recording found for this entry.');
      } else if (err?.status === 403) {
        this.showToastMessage('You do not have permission to download recordings.');
      } else {
        this.showToastMessage('Could not download the audio recording. Please try again.');
      }
    } finally {
      this.downloadingAudioId = null;
      this.cdr.markForCheck();
    }
  }

  /** `<Surname> <Name> - <title> - <YYYY-MM-DD>.wav`, with filename-illegal characters stripped. */
  private buildEncounterAudioFileName(item: any): string {
    const rawPatient = [this.patient?.surname, this.patient?.name].filter(Boolean).join(' ');
    const patientPart = this.sanitizeFileNamePart(rawPatient) || 'Patient';
    const titlePart = this.sanitizeFileNamePart((item?.title ?? '').toString()) || 'Recording';
    const dateValue = item?.date || item?.createdAt;
    const dateMoment = moment(dateValue);
    const datePart = dateMoment.isValid() ? dateMoment.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');

    return `${patientPart} - ${titlePart} - ${datePart}.wav`;
  }

  /** Strip characters that are illegal in Windows/POSIX filenames and collapse whitespace. */
  private sanitizeFileNamePart(value: string): string {
    // eslint-disable-next-line no-control-regex
    return value.replace(/[<>:"/\\|?*\x00-\x1F]/g, '').replace(/\s+/g, ' ').trim();
  }

  handleMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }

  emailCommunication(req: Omit<SendAIContentEmailRequest, 'speciality'>): void {
    this.patientCommunicationService.sendAIContentEmail({ ...req, speciality: '' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Email sent successfully');
          this.closeAllMenus(this.menuTriggers);
        },
        error: (error) => {
          console.error('Error sending AI email: ', error);
          this.showToastMessage('There was an error sending the email. Please try again.');
        }
      });
  }

  openWhatsAppDialog(item?: any): void {
    let plainTextContent = '';
    if (item) {
      const recordType = item.type === 'note' ? item.notes : this.compileAdmissionRecord(item);
      plainTextContent = this.pdfDownloadService.convertToPlainText(recordType);
    }

    const dialogRef = this.dialog.open(WhatsAppChatDialogComponent, {
      width: '900px',
      maxWidth: '90vw',
      height: '80vh',
      maxHeight: '90vh',
      data: {
        patientId: this.patient?.id,
        patient: this.patient,
        initialMessage: plainTextContent
      }
    });
  }

  openWhatsAppDialogFromTimeline(whatsappItem: any): void {
    const dialogRef = this.dialog.open(WhatsAppChatDialogComponent, {
      width: '900px',
      maxWidth: '90vw',
      height: '80vh',
      maxHeight: '90vh',
      data: {
        patientId: this.patient?.id,
        patient: this.patient
      }
    });
  }

  /*
   * `openReturnedDocument()` WAS HERE — the open-in-new button on a returned-document row, which
   * called `PatientDocumentRequestService.focusRequest` to jump to the documents panel.
   *
   * Gone with the button, 10 Sep 2026, owner's call: the row draws the returned pages itself now,
   * so the reason to leave the timeline to look at them went with it. Reviewing still happens on
   * the panel — it owns Accept / Reject — and the panel is one click away in the same file.
   *
   * NOTE FOR WHOEVER NEEDS IT NEXT: this was the ONLY caller of that service's `focusRequest`, so
   * `focusRequestSubject` / `focusRequest()` / `focusRequest$` and the panel's subscription to it
   * now have no producer. They are left in place rather than swept up: the identical mechanism on
   * `patient-form-request.service.ts` is live, this one is two lines, and it is the obvious hook
   * for anything that later wants to send a user to one request.
   */

  /**
   * Convert item to plain text for WhatsApp dialog
   * Used by the WhatsApp dialog trigger directive
   */
  getItemPlainText(item?: any): string {
    if (!item) {
      return '';
    }

    let recordType: string;
    if (item.type === 'note') {
      // Regular note (clinical, procedure, morbidity, mortality)
      recordType = item.notes || '';
    } else if (item.type === 'admission-record') {
      // Admission record
      recordType = this.compileAdmissionRecord(item);
    } else if (item.type === TIMELINE_DOCUMENT_TYPE) {
      // §26. Without this the WhatsApp trigger would fall through to `item.notes` — a field a
      // document row does not have — and open with an empty message.
      recordType = compileDocumentReturn(item);
    } else {
      // Fallback
      recordType = item.notes || '';
    }

    return this.pdfDownloadService.convertToPlainText(recordType);
  }

  /**
   * Get media from item for WhatsApp dialog
   * Filters out PDFs (same as email/PDF download)
   * Used by the WhatsApp dialog trigger directive
   */
  getItemMedia(item?: any): any[] {
    if (!item || !item.media || !Array.isArray(item.media)) {
      return [];
    }
    // Filter out PDFs (same pattern as email/PDF download)
    return item.media.filter((media: any) => media.mediaType !== 'application/pdf');
  }

  openRestoreDialog(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.patient) {
      return;
    }

    const dialogRef = this.dialog.open(RestoreTimelineDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      data: {
        patientId: this.patient.id
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(() => {
      // Reload timeline data after dialog closes in case items were restored
      this.loadTimelineData();
    });
  }

  checkHasProcessingPdf(media: any[]): boolean {
    if (!media || !Array.isArray(media)) {
      return false;
    }
    return media.some((m: any) =>
      m.mediaType === 'application/pdf' && m.processingStatus === 'processing'
    );
  }

  /** True when the item has at least one embeddable (non-PDF) attachment for timeline PDF/email. */
  hasTimelinePdfMedia(item: any): boolean {
    return !!item?.media?.some((m: any) => m.mediaType !== 'application/pdf');
  }

  applyFilters(): void {
    if (!this.timelineData) {
      this.contentFilteredTimelineData = [];
      this.filteredTimelineData = [];
      this.searchHitsById.clear();
      this.searchNarrowingIds = null;
      this.startBatchRendering([]);
      this.scheduleSearchCorpusRebuild([]);
      return;
    }

    this.searchHitsById.clear();
    this.searchNarrowingIds = null;
    this.contentFilteredTimelineData = this.computeContentFilteredGroups();
    this.refreshTimelineViewForSearch();
    this.scheduleSearchCorpusRebuild(this.flattenTimelineItems(this.contentFilteredTimelineData));
  }

  onTimelineSearchInput(value: string): void {
    this.timelineSearchQuery = value;
    this.timelineSearchQuery$.next(value);
  }

  clearTimelineSearch(): void {
    this.timelineSearchQuery = '';
    this.timelineSearchQuery$.next('');
    this.debouncedSearchQuery = '';
    this.searchHitsById.clear();
    this.searchNarrowingIds = null;
    this.timelineSearchInFlight = false;
    this.refreshTimelineViewForSearch();
    this.cdr.markForCheck();
  }

  isTimelineSearchHighlightMode(): boolean {
    return (
      this.debouncedSearchQuery.trim().length >= TIMELINE_SEARCH_MIN_QUERY &&
      !this.timelineSearchInFlight &&
      this.searchNarrowingIds !== null
    );
  }

  getTimelineItemSearchId(item: any): string {
    if (!item) {
      return '';
    }
    if (item.type === 'whatsapp-communication') {
      return `whatsapp:${item.date}`;
    }
    if (item.type === 'patient-chat') {
      return `patient-chat:${item.date}`;
    }
    if (item.type === TIMELINE_DOCUMENT_TYPE) {
      // Keyed on the REQUEST id: a document that is rejected and returned again is the same row
      // (§13.2), so a key built from the return date would make one ask look like two.
      return `document:${item.id}`;
    }
    if (item.type === 'admission-record') {
      return `admission:${item.id}`;
    }
    if (item.type === 'note') {
      return `note:${item.id}`;
    }
    return `item:${item.id ?? ''}`;
  }

  private resetTimelineSearchUiState(): void {
    this.timelineSearchQuery = '';
    this.debouncedSearchQuery = '';
    this.searchHitsById.clear();
    this.searchNarrowingIds = null;
    this.timelineSearchInFlight = false;
    this.timelineSearchIndexing = false;
    this.rawCorpusBuildToken++;
  }

  private onDebouncedTimelineSearchChange(): void {
    const trimmed = this.debouncedSearchQuery.trim();
    if (trimmed.length < TIMELINE_SEARCH_MIN_QUERY) {
      this.searchHitsById.clear();
      this.searchNarrowingIds = null;
      this.timelineSearchInFlight = false;
      this.refreshTimelineViewForSearch();
      this.cdr.markForCheck();
      return;
    }
    if (this.timelineSearchIndexing) {
      return;
    }
    this.beginTimelineSearchRequest(trimmed);
  }

  private beginTimelineSearchRequest(trimmed: string): void {
    if (trimmed.length < TIMELINE_SEARCH_MIN_QUERY) {
      return;
    }
    this.searchNarrowingIds = null;
    this.timelineSearchInFlight = true;
    this.refreshTimelineViewForSearch();
    this.timelineSearchService.search(this.currentCorpusId, trimmed);
    this.cdr.markForCheck();
  }

  private refreshTimelineViewForSearch(): void {
    let data = [...this.contentFilteredTimelineData];
    const q = this.debouncedSearchQuery.trim();
    if (q.length >= TIMELINE_SEARCH_MIN_QUERY && this.searchNarrowingIds !== null) {
      const ids = this.searchNarrowingIds;
      data = data
        .map((group) => ({
          ...group,
          items: (group.items || []).filter((item: any) => ids.has(this.getTimelineItemSearchId(item)))
        }))
        .filter((group: any) => group.items.length > 0);
    }
    this.filteredTimelineData = data;
    this.startBatchRendering(data);
  }

  private flattenTimelineItems(groups: any[]): any[] {
    if (!groups?.length) {
      return [];
    }
    return groups.flatMap((g) => g.items || []);
  }

  private computeContentFilteredGroups(): any[] {
    const hasActiveFilters = Object.values(this.filters).some((filter) => filter);
    const hasDateRangeFilter = this.dateRange.enabled && (this.dateRange.startDate || this.dateRange.endDate);

    if (!this.timelineData) {
      return [];
    }

    if (!hasActiveFilters && !hasDateRangeFilter) {
      return [...this.timelineData];
    }

    return this.timelineData
      .map((group) => {
        const filteredItems = group.items.filter((item: any) => {
          let matchesContentFilter = false;
          let matchesDateFilter = true;

          if (item.type === 'note') {
            if (this.filters.clinicalNotes && !item.isProcedure && !item.isMorbidity && !item.isMortality && !item.isBilling) {
              matchesContentFilter = true;
            }
            if (this.filters.procedureNotes && item.isProcedure) {
              matchesContentFilter = true;
            }
            if (this.filters.billingNotes && item.isBilling) {
              matchesContentFilter = true;
            }
            if (this.filters.morbidity && item.isMorbidity) {
              matchesContentFilter = true;
            }
            if (this.filters.mortality && item.isMortality) {
              matchesContentFilter = true;
            }
            if (this.filters.withMedia && item.media && item.media.length > 0) {
              matchesContentFilter = true;
            }
          }

          if (item.type === 'admission-record' && this.filters.admissionRecords) {
            matchesContentFilter = true;
          }

          if (this.dateRange.enabled) {
            const itemDate = item.date ? moment(item.date) : moment(item.createdAt);

            if (this.dateRange.startDate) {
              const startDate = moment(this.dateRange.startDate);
              matchesDateFilter = matchesDateFilter && itemDate.isSameOrAfter(startDate, 'day');
            }

            if (this.dateRange.endDate) {
              const endDate = moment(this.dateRange.endDate);
              matchesDateFilter = matchesDateFilter && itemDate.isSameOrBefore(endDate, 'day');
            }
          }

          if (!hasActiveFilters) {
            return matchesDateFilter;
          }

          return matchesContentFilter && matchesDateFilter;
        });

        return {
          ...group,
          items: filteredItems
        };
      })
      .filter((group) => group.items.length > 0);
  }

  private scheduleSearchCorpusRebuild(flatItems: any[]): void {
    this.timelineSearchIndexing = true;
    this.rawCorpusBuildToken++;
    const token = this.rawCorpusBuildToken;
    const raw: import('./clinical-timeline-search-core').ClinicalTimelineSearchRawItem[] = [];
    let i = 0;

    const step = () => {
      if (token !== this.rawCorpusBuildToken) {
        return;
      }
      const end = Math.min(i + TIMELINE_RAW_BUILD_SLICE, flatItems.length);
      for (; i < end; i++) {
        raw.push(this.buildRawSearchItem(flatItems[i]));
      }
      if (i < flatItems.length) {
        const ric = (typeof requestIdleCallback !== 'undefined' ? requestIdleCallback : null) as
          | ((cb: () => void, opts?: { timeout?: number }) => number)
          | null;
        if (ric) {
          ric(() => step(), { timeout: 400 });
        } else {
          setTimeout(step, 0);
        }
      } else {
        if (token !== this.rawCorpusBuildToken) {
          return;
        }
        this.currentCorpusId = this.timelineSearchService.setCorpus(raw);
      }
    };

    step();
  }

  private buildRawSearchItem(item: any): import('./clinical-timeline-search-core').ClinicalTimelineSearchRawItem {
    const id = this.getTimelineItemSearchId(item);
    const empty = {
      id,
      title: '',
      body: '',
      footer: '',
      heading: '',
      dates: '',
      diagnosis: '',
      summary: '',
      hospital: '',
      discharge: '',
      text: ''
    };

    if (item.type === 'patient-chat') {
      return {
        ...empty,
        heading: 'Patient chat',
        text: `Patient chat ${this.chatDayLine(item)}`
      };
    }

    if (item.type === 'whatsapp-communication') {
      return {
        ...empty,
        heading: 'WhatsApp Communication',
        text: `WhatsApp Communication ${item.messageCount} message${item.messageCount !== 1 ? 's' : ''} sent to ${item.contactCount} contact${item.contactCount !== 1 ? 's' : ''}`
      };
    }

    if (item.type === TIMELINE_DOCUMENT_TYPE) {
      // The AI summary is NOT indexed, and `documentSearchText` says why: the list endpoint does
      // not serve it, so indexing it would mean shipping every patient's encrypted summaries to
      // the browser to make them findable.
      return {
        ...empty,
        heading: 'Returned Document',
        text: documentSearchText(item)
      };
    }

    if (item.type === 'admission-record') {
      const extraDx = this.getDiagnosisArray(item).filter(Boolean).join(' ');
      const admissionDate = this.safeFormatDate(item.admissionDate, 'DD/MM/YYYY');
      const dischargeDate = this.safeFormatDate(item.dischargeDate, 'DD/MM/YYYY');
      const dateDiff =
        item.admissionDate && item.dischargeDate ? this.dateDiffPipe.transform(item.admissionDate, item.dischargeDate) : 'N/A';
      const dates = `${admissionDate} to ${dischargeDate} (${dateDiff})`;
      const diagnosis = [item.diagnosis, extraDx].filter(Boolean).join(' ');
      const summary = [item.admissionType, item.admissionSummary].filter(Boolean).join(' - ');
      const hospital =
        item.hospital || item.ward
          ? `${item.hospital || ''} (${item.ward || ''})`.replace(/\(\s*\)/, '').trim()
          : '';
      return {
        ...empty,
        heading: 'Admission Record',
        dates,
        diagnosis,
        summary,
        hospital,
        discharge: item.dischargeSummary || '',
        dischargeMd: true,
        footer: item.dischargedBy ? `Discharged By: ${item.dischargedBy}` : ''
      };
    }

    const createdFooter = item.createdBy
      ? `Created By: ${item.createdBy} at ${moment(item.createdAt).format('HH:mm')}`
      : '';

    if (item.type === 'note' && item.newWard) {
      let body = '';
      if (item.previousWard && item.newWard) {
        body = `From ${item.previousWard} to ${item.newWard}`;
      } else if (!item.previousWard && item.newWard) {
        body = `Admitted to ${item.newWard}`;
      } else if (item.previousWard && !item.newWard) {
        body = `Discharged from ${item.previousWard}`;
      }
      return {
        ...empty,
        title: 'Ward Movement',
        body,
        footer: createdFooter
      };
    }

    if (item.type === 'note') {
      return {
        ...empty,
        title: this.getNoteSearchTitle(item),
        body: item.notes || '',
        bodyMd: true,
        footer: createdFooter
      };
    }

    return empty;
  }

  private getNoteSearchTitle(item: any): string {
    if (item.isMorbidity) {
      return `Morbidity: ${item.title || ''}`;
    }
    if (item.isMortality) {
      return `Mortality: ${item.title || ''}`;
    }
    if (item.isBilling) {
      return `Billing: ${item.title || ''}`;
    }
    return item.title || '';
  }

  private fuseIndicesForKey(
    matches: FuseResult<ClinicalTimelineSearchIndexRecord>['matches'],
    key: keyof ClinicalTimelineSearchIndexRecord
  ): ReadonlyArray<readonly [number, number]> {
    if (!matches?.length) {
      return [];
    }
    const out: [number, number][] = [];
    for (const m of matches) {
      if (m.key === key && m.indices) {
        for (const pair of m.indices) {
          out.push([pair[0], pair[1]]);
        }
      }
    }
    return out;
  }

  timelineSearchHtmlForField(item: any, key: keyof ClinicalTimelineSearchIndexRecord): SafeHtml {
    const hit = this.searchHitsById.get(this.getTimelineItemSearchId(item));
    const text = hit?.record[key] ?? '';
    const idx = pruneTrivialHighlightSpans(
      this.fuseIndicesForKey(hit?.matches, key),
      this.debouncedSearchQuery
    );
    const html = applyHighlightMarks(text, idx);
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  timelineSearchHtmlForWardBody(item: any): SafeHtml {
    const hit = this.searchHitsById.get(this.getTimelineItemSearchId(item));
    const text = hit?.record.body ?? '';
    const idx = pruneTrivialHighlightSpans(
      this.fuseIndicesForKey(hit?.matches, 'body'),
      this.debouncedSearchQuery
    );
    const html = applyHighlightMarks(text, idx);
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Start rendering timeline data in batches
   * Always shows the most recent items first
   * Renders only the first batch initially
   */
  private startBatchRendering(data: any[]): void {
    // Reset displayed data and batch index
    this.displayedTimelineData = [];
    this.currentBatchIndex = 0;

    if (!data || data.length === 0) {
      this.hasMoreItems = false;
      return;
    }

    // Render first batch immediately (most recent items)
    this.loadNextBatch();
  }

  /**
   * Load the next batch of timeline groups
   * Preserves scroll position when loading more items
   */
  loadNextBatch(): void {
    if (!this.filteredTimelineData || this.currentBatchIndex >= this.filteredTimelineData.length) {
      this.hasMoreItems = false;
      return;
    }

    // Find the scrollable container - check common scroll containers
    let scrollContainer: Element | Window | null = null;
    let scrollPosition: number = 0;

    // Try to find the actual scrollable parent element
    const timelineElement = document.querySelector('.clinical-timeline-info');
    if (timelineElement) {
      // Find the scrollable parent
      let parent = timelineElement.parentElement;
      while (parent && parent !== document.body) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          scrollContainer = parent;
          scrollPosition = parent.scrollTop;
          break;
        }
        parent = parent.parentElement;
      }
    }

    // Fallback to window scroll if no scrollable container found
    if (!scrollContainer) {
      scrollContainer = window;
      scrollPosition = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    }

    // Calculate how many items to load
    const endIndex = Math.min(this.currentBatchIndex + this.batchSize, this.filteredTimelineData.length);
    const batch = this.filteredTimelineData.slice(this.currentBatchIndex, endIndex);

    // Directly push to array instead of creating new array reference
    // This preserves object references for existing items, preventing re-renders
    this.displayedTimelineData.push(...batch);
    this.currentBatchIndex = endIndex;

    // Update hasMoreItems flag
    this.hasMoreItems = this.currentBatchIndex < this.filteredTimelineData.length;

    // The batch that just landed may contain returned-document rows whose pages have never been
    // read. Here rather than in the data build, because the build runs for the WHOLE timeline and
    // this runs for what is actually on screen — see `hydrateDocumentGalleries`.
    this.hydrateDocumentGalleries();

    // Use markForCheck() to schedule change detection
    this.cdr.markForCheck();

    // Restore scroll position after Angular renders the new items
    // Use requestAnimationFrame for better timing with DOM updates
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (scrollContainer === window) {
          window.scrollTo(0, scrollPosition);
        } else if (scrollContainer) {
          (scrollContainer as Element).scrollTop = scrollPosition;
        }
      });
    });
  }

  /**
   * TrackBy function for ngFor to help Angular identify items
   * This prevents re-rendering of existing items when new batches are added
   */
  trackByGroupDate(index: number, group: any): string {
    return group.date || index.toString();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

}